import { useCallback, useState } from 'react'
import * as R from 'remeda'
import * as z from 'zod'

export const useFormValidation = <T extends z.ZodRawShape>(schema: z.ZodObject<T>) => {
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({})
  const clearErrors = useCallback(() => {
    setErrors({})
  }, [])

  const validate = useCallback(
    (data: z.infer<z.ZodObject<T>>): boolean => {
      try {
        schema.parse(data)
        clearErrors()

        return true
      } catch (error) {
        if (error instanceof z.ZodError) {
          const newErrors: Partial<Record<keyof T, string>> = {}
          error.issues.forEach((err) => {
            if (err.path.length > 0) {
              const field = err.path[0] as keyof T
              newErrors[field] = err.message
            }
          })
          setErrors(newErrors)
        }

        return false
      }
    },
    [schema, clearErrors],
  )
  const clearFieldError = useCallback((field: keyof T) => {
    setErrors((prev) => {
      return {
        ...prev,
        [field]: undefined,
      }
    })
  }, [])

  const setFieldError = useCallback((field: keyof T, message: string) => {
    setErrors((prev) => ({
      ...prev,
      [field]: message,
    }))
  }, [])

  const validateField = useCallback(
    (field: keyof T, value: unknown): boolean => {
      try {
        // zod v4 types object `.shape` values as the core `$ZodType`, which lacks
        // the `.parse` method; narrow to the full `ZodType` (they are at runtime).
        const fieldSchema = schema.shape[field] as z.ZodType | undefined
        if (!fieldSchema) {
          return true
        }

        fieldSchema.parse(value)
        clearFieldError(field)

        return true
      } catch (error) {
        if (error instanceof z.ZodError) {
          setFieldError(field, error.issues[0]?.message ?? '')
        }

        return false
      }
    },
    [schema.shape, clearFieldError, setFieldError],
  )

  const hasErrors = (R.values(errors) as (string | undefined)[]).some(R.isNonNullish)

  return {
    isValid: !hasErrors,
    errors,
    validate,
    clearErrors,
    clearFieldError,
    validateField,
    setFieldError,
  }
}
